import React, { useEffect, useState } from 'react';
import { Label, Mullions } from './System';
import Icon from './Icon';

function IndexList({ sections, active, onPick }) {
  return (
    <ol className="m-0 list-none p-0">
      {sections.map((s) => {
        const on = s.id === active;
        return (
          <li key={s.id} className="border-t border-wall/15">
            <a
              href={`#${s.id}`}
              onClick={onPick}
              aria-current={on ? 'location' : undefined}
              className={`relative flex min-h-[44px] items-center py-2 pl-3 text-[15px] leading-snug transition-colors ${
                on ? 'font-semibold text-wall' : 'text-glass hover:text-wall'
              }`}
            >
              <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[2px] ${on ? 'bg-bronze' : 'bg-transparent'}`} />
              {s.label}
            </a>
          </li>
        );
      })}
    </ol>
  );
}

// Which section is under the reading line. When two share the band, the later one has just arrived.
export function useScrollSpy(sections) {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const inBand = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) inBand.add(e.target.id);
          else inBand.delete(e.target.id);
        });
        const current = sections.filter((s) => inBand.has(s.id)).pop();
        if (current) setActive(current.id);
      },
      { rootMargin: '-18% 0px -72% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [sections]);

  return active;
}

// The technical memorandum: a sticky section index (scroll-spy on desktop, collapsible on mobile)
// beside long-form content in a readable measure.
function TechDocLayout({ sections, indexLabel = 'In this memo', children }) {
  const active = useScrollSpy(sections);

  const closeIndex = (e) => {
    const details = e.currentTarget.closest('details');
    if (details) details.open = false;
  };

  return (
    <section className="relative bg-limestone">
      <Mullions />
      <div className="shell">
        <div className="grid-12 relative pb-20 pt-8 lg:pb-32 lg:pt-16">
          <nav aria-label="Sections" className="col-span-4 lg:col-span-3">
            <details className="group mb-8 border-y border-wall/25 lg:hidden">
              <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between font-semibold [&::-webkit-details-marker]:hidden">
                {indexLabel}
                <Icon name="chevron" className="transition-transform group-open:rotate-180" />
              </summary>
              <div className="pb-3">
                <IndexList sections={sections} active={active} onPick={closeIndex} />
              </div>
            </details>
            <div className="sticky top-[96px] hidden lg:block">
              <Label muted className="mb-4">{indexLabel}</Label>
              <div className="border-b border-wall/15">
                <IndexList sections={sections} active={active} />
              </div>
            </div>
          </nav>
          <div className="col-span-4 min-w-0 lg:col-span-9 xl:col-span-8 xl:col-start-5">{children}</div>
        </div>
      </div>
    </section>
  );
}

export function DocSection({ id, label, title, intro, children }) {
  return (
    <section id={id} className="border-t border-wall/25 pb-16 pt-8 first:border-t-0 first:pt-0 lg:pb-24">
      {label && <Label className="mb-4">{label}</Label>}
      {title && <h2 className="type-h2 mb-5">{title}</h2>}
      {intro && <div className="type-lead mb-8 space-y-3 text-glass">{intro}</div>}
      {children}
    </section>
  );
}

export default TechDocLayout;
